import React, { useCallback, useState } from "react";
import { Box, Container, Grid, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import BackgroundImageCst from "@components/BackgroundImageCst";
import ButtonBackPage from "@components/ButtonBackPage";
import ButtonCst from "@components/ButtonCst";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";

import { CorporateBrandAssetDto } from "./types";
import { GLOBAL_CORPORATE_ID } from "./constants";
import { useBrandAssets } from "./hooks/useBrandAssets";
import ModelCard from "./components/ModelCard";
import ModelEditorDialog from "./components/ModelEditorDialog";

const BrandAsset: React.FC = () => {
  const { t } = useTranslation(["corporate-brand-asset", "modal"]);
  const { id } = useParams();
  const corporateId = Number(id);
  const isGlobalScope = corporateId === GLOBAL_CORPORATE_ID;

  const { dataDetail: corporate } = useFetchDetail(
    ServicesURL.corporate,
    id || "",
    PagesID.corporate,
  );

  const { dataDetail: globalCorporate } = useFetchDetail(
    ServicesURL.corporate,
    isGlobalScope ? "" : `${GLOBAL_CORPORATE_ID}`,
    PagesID.corporate,
  );

  const corporateName = corporate?.name || "";
  const globalCorporateName = isGlobalScope
    ? corporateName
    : globalCorporate?.name || "";

  const {
    list,
    globalList,
    isLoading,
    mutate,
    createBrandAsset,
    editBrandAsset,
    deleteBrandAsset,
  } = useBrandAssets(corporateId);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<"create" | "edit">("create");
  const [editingDto, setEditingDto] = useState<CorporateBrandAssetDto>();
  const [modelToDelete, setModelToDelete] = useState<CorporateBrandAssetDto>();
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();

  const handleAdd = () => {
    setEditorMode("create");
    setEditingDto(undefined);
    setEditorOpen(true);
  };

  const handleEdit = (model: CorporateBrandAssetDto) => {
    setEditorMode("edit");
    setEditingDto(model);
    setEditorOpen(true);
  };

  const handleSaved = useCallback(() => {
    setEditorOpen(false);
    mutate();
    setOpenToast({ s: StatusEnum.OK, message: "" });
  }, [mutate]);

  const handleConfirmDelete = useCallback(async () => {
    if (!modelToDelete) return;
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
    try {
      await deleteBrandAsset(modelToDelete.id);
      mutate();
      setModelToDelete(undefined);
      setOpenToast({ s: StatusEnum.OK, message: "" });
    } catch (error) {
      setModelToDelete(undefined);
      setOpenToast(undefined);
      console.error(error);
    }
  }, [modelToDelete, deleteBrandAsset, mutate]);

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-corporate.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container maxWidth={"xxl"} sx={{ position: "relative", zIndex: 1 }}>
        <HeroCst
          title={t(isGlobalScope ? "hero.titleGlobal" : "hero.titleDedicated")}
          description={t(
            isGlobalScope
              ? "hero.descriptionGlobal"
              : "hero.descriptionDedicated",
          )}
        />
        <ButtonBackPage />
        <Box
          sx={{
            mt: "25px",
            display: "flex",
            justifyContent: "flex-end",
            mb: 2,
          }}
        >
          <ButtonCst
            id="brand-asset-add"
            variant="contained"
            onClick={handleAdd}
            disabled={!corporateName}
          >
            <AddIcon fontSize="small" sx={{ mr: 0.5 }} />
            {t("actions.add")}
          </ButtonCst>
        </Box>

        {!isLoading && list.length === 0 ? (
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            {t("emptyList")}
          </Typography>
        ) : (
          <Grid container spacing={3}>
            {list.map((model) => (
              <Grid item xs={12} sm={6} md={4} key={model.id}>
                <ModelCard
                  model={model}
                  onEdit={handleEdit}
                  onDelete={setModelToDelete}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {editorOpen && (
        <ModelEditorDialog
          open={editorOpen}
          mode={editorMode}
          editingDto={editingDto}
          corporateId={corporateId}
          isGlobalScope={isGlobalScope}
          sourceModels={globalList}
          corporateName={corporateName}
          globalCorporateName={globalCorporateName}
          createBrandAsset={createBrandAsset}
          editBrandAsset={editBrandAsset}
          onClose={() => setEditorOpen(false)}
          onSaved={handleSaved}
        />
      )}

      <ModalConfirmsCst
        open={!!modelToDelete}
        title={t("delete-item", { ns: "modal" })}
        description={t("confirm-delete", { ns: "modal" })}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setModelToDelete(undefined);
          setOpenToast(undefined);
        }}
        onActionConfirmed={handleConfirmDelete}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};

export default BrandAsset;
